import { NavLink, Outlet, Link, useLocation } from 'react-router-dom'
import { useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Logo } from './Logo'
import { useSession } from '../lib/session'
import { currentTheme, toggleTheme, type Theme } from '../lib/theme'

interface NavItem {
  to: string
  label: string
  permission?: string
  feature?: string
}

/**
 * The reader's five sections, in the order the boards set: Search, Browse,
 * Clippings, Saved, Timeline. Review appears for an editor and Admin for an
 * operator; neither is a reader section and both sit after the five.
 */
const NAV: NavItem[] = [
  { to: '/search', label: 'Search' },
  { to: '/browse', label: 'Browse' },
  { to: '/clippings', label: 'Clippings', permission: 'research.clipping' },
  { to: '/saved', label: 'Saved', permission: 'research.saved_search' },
  { to: '/timeline', label: 'Timeline', feature: 'research.timeline' },
  { to: '/review', label: 'Review', permission: 'review.queue.view' },
  { to: '/admin', label: 'Admin', permission: 'admin.ingest.manage' },
]

/**
 * The handbook. It lives outside the single-page application (MkDocs, served
 * at /docs), so it is a plain anchor rather than a route: a client-side link
 * would try to match it against the router and land on the fallback.
 */
const DOCS_HREF = '/docs/handbook/'

/**
 * The reader chrome: one 46px bar, and everything else is the work.
 *
 * A horizontal bar rather than a rail because the page viewer needs every
 * pixel of width it can get, and a broadsheet is 792 points wide against a
 * laptop's 1280. Chrome overlays or gets out of the way; it never takes room
 * from the page.
 *
 * The active link is marked with an accent underline rather than a filled
 * pill, so the accent stays reserved for its four jobs and the chrome stays
 * quiet next to the scan.
 *
 * A screen may put its own control in the middle of the bar (the viewer puts
 * its search-in-page box there) through `ReaderBarSlot`; the bar itself never
 * knows what the screen is.
 */
export function ReaderLayout() {
  const { config, me, can, hasFeature, signOut } = useSession()
  const [theme, setTheme] = useState<Theme>(currentTheme())
  const location = useLocation()
  // The viewer owns the whole height and scrolls inside itself; every other
  // screen scrolls as a document.
  const fullHeight =
    location.pathname.startsWith('/page/') ||
    location.pathname.startsWith('/review') ||
    location.pathname.startsWith('/compare')
  // On a phone the page owns the whole screen (board 04): the reader bar
  // gives way to the viewer's own sheet. On a tablet the section links give
  // way to the viewer's breadcrumb and search box; the mark, the seat and
  // the account stay.
  const viewer = location.pathname.startsWith('/page/')
  const sections = NAV.filter(
    (item) =>
      (!item.permission || can(item.permission)) && (!item.feature || hasFeature(item.feature)),
  )

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-paper">
      <header
        className={`h-[46px] flex-none items-center gap-4 border-b border-n300 px-4 ${
          viewer ? 'hidden sm:flex' : 'flex'
        }`}
      >
        <Link
          to={me ? '/search' : '/'}
          className="flex flex-none items-center gap-2.5 border-r border-n300 pr-4 text-body no-underline"
        >
          <Logo size={16} />
          <span className="hidden whitespace-nowrap text-[15px] font-semibold sm:inline">
            {config?.product_name ?? ''}
          </span>
        </Link>

        <nav
          aria-label="Sections"
          className={`min-w-0 gap-4 overflow-x-auto ${viewer ? 'hidden lg:flex' : 'hidden sm:flex'}`}
        >
          {sections.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `whitespace-nowrap text-[13.5px] no-underline ${
                  isActive
                    ? 'font-semibold text-body shadow-[0_8px_0_-6px_var(--jl-accent)]'
                    : 'text-accent'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <a href={DOCS_HREF} className="whitespace-nowrap text-[13.5px] text-accent no-underline">
            Handbook
          </a>
        </nav>

        <div id="reader-bar-slot" className="hidden min-w-0 flex-1 items-center md:flex" />

        <div className="ml-auto flex flex-none items-center gap-3">
          {me ? <SeatPill /> : <GuestPill />}
          <button
            type="button"
            className="btn-ghost text-[13px]"
            onClick={() => setTheme(toggleTheme())}
            aria-label={`Switch to the ${theme === 'dark' ? 'light' : 'dark'} theme`}
          >
            {theme === 'dark' ? 'Light' : 'Dark'}
          </button>
          {me ? (
            <>
              <button type="button" className="btn-ghost text-[13px]" onClick={() => void signOut()}>
                Sign out
              </button>
              <Link to="/account" className="no-underline" title="Account and access status">
                <Initials email={me.email} name={me.display_name} />
              </Link>
            </>
          ) : (
            <Link to="/login" className="btn-primary no-underline">
              Sign in
            </Link>
          )}
        </div>
      </header>

      <main
        className={`flex min-h-0 min-w-0 flex-1 flex-col ${fullHeight ? 'overflow-hidden' : 'overflow-y-auto'}`}
      >
        <Outlet />
      </main>

      {!viewer && <PhoneTabs sections={sections} signedIn={!!me} />}
    </div>
  )
}

/**
 * The sections on a phone: a tab bar along the foot of the screen, as the
 * prototype set it, because a bar of seven links does not fit across 390
 * pixels and the ones that fell off the edge could not be reached. The first
 * four are tabs; the rest, the handbook and the account are under More. The
 * viewer has its own sheet and owns the whole screen (board 04), so it gets
 * none.
 */
function PhoneTabs({ sections, signedIn }: { sections: NavItem[]; signedIn: boolean }) {
  const [more, setMore] = useState(false)
  const location = useLocation()
  const [path, setPath] = useState(location.pathname)
  if (path !== location.pathname) {
    // A tab was followed: the More list closes behind it.
    setPath(location.pathname)
    setMore(false)
  }
  const tabs = sections.slice(0, 4)
  const rest = sections.slice(4)
  // A section under More marks More as where the reader is.
  const inRest =
    location.pathname.startsWith('/account') ||
    rest.some((item) => location.pathname.startsWith(item.to))
  const tab =
    'flex min-h-[48px] flex-1 items-center justify-center px-1 text-[11px] uppercase tracking-[0.11em] no-underline'
  const row = 'block border-b border-n200 py-3 text-[15px] no-underline'

  return (
    <div className="flex-none sm:hidden">
      {more && (
        <div className="border-t border-n300 bg-surface px-4 pb-1 pt-1">
          {rest.map((item) => (
            <NavLink key={item.to} to={item.to} className={row}>
              {item.label}
            </NavLink>
          ))}
          <a href={DOCS_HREF} className={row}>
            Handbook
          </a>
          {signedIn ? (
            <Link to="/account" className={row}>
              Account
            </Link>
          ) : (
            <Link to="/login" className={row}>
              Sign in
            </Link>
          )}
        </div>
      )}
      <nav
        aria-label="Sections"
        className="flex border-t border-n300 bg-surface pb-[env(safe-area-inset-bottom)]"
      >
        {tabs.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `${tab} ${
                isActive
                  ? 'font-semibold text-body shadow-[inset_0_2px_0_var(--jl-accent)]'
                  : 'text-ink2'
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
        <button
          type="button"
          aria-expanded={more}
          onClick={() => setMore((open) => !open)}
          className={`${tab} ${
            more ? 'font-semibold text-body' : inRest ? 'font-semibold text-body shadow-[inset_0_2px_0_var(--jl-accent)]' : 'text-ink2'
          }`}
        >
          More
        </button>
      </nav>
    </div>
  )
}

/**
 * A screen's own control in the middle of the reader bar, rendered into the
 * slot by a portal. The viewer uses it for search-in-page.
 */
export function ReaderBarSlot({ children }: { children: ReactNode }) {
  const [target] = useState(() => document.getElementById('reader-bar-slot'))
  if (!target) return null
  // Lazy import keeps the portal API out of screens that never use it.
  return <Portal target={target}>{children}</Portal>
}

function Portal({ target, children }: { target: HTMLElement; children: ReactNode }) {
  return createPortal(children, target)
}

/**
 * The seat indicator. It is present on every screen for a signed-in reader,
 * because on a one-seat licence the seat is the scarcest thing in the product
 * and a reader who cannot see it is a reader who is surprised by losing it.
 */
function SeatPill() {
  const { me, can } = useSession()
  const role = me?.roles?.[0] ?? 'reader'
  const api = can('content.api')
  return (
    <div
      className="hidden items-center gap-1.5 rounded border px-2.5 py-[3px] md:flex"
      style={{ background: 'var(--jl-accent-100)', borderColor: 'var(--jl-accent-200)' }}
      title="Your role and what it permits"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-art" />
      <span className="font-mono text-[10px] uppercase text-accent">
        {role}
        {api ? ' · API on' : ''}
      </span>
    </div>
  )
}

/** A guest is told plainly what tier they are on, rather than left to guess. */
function GuestPill() {
  return (
    <div className="hidden items-center gap-1.5 rounded border border-rule2 px-2.5 py-[3px] md:flex">
      <span className="h-1.5 w-1.5 rounded-full bg-ink4" />
      <span className="font-mono text-[10px] uppercase text-ink2">Guest · metadata only</span>
    </div>
  )
}

export function Initials({ email, name }: { email: string; name?: string | null }) {
  const source = (name || email).trim()
  const letters = source.includes(' ')
    ? source
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
    : source.slice(0, 2)
  return (
    <div
      className="flex h-[26px] w-[26px] items-center justify-center rounded border border-rule2 text-[12px] font-semibold text-ink2"
      title={email}
    >
      {letters.toUpperCase()}
    </div>
  )
}
